import React, { useEffect, useMemo, useRef, useState } from 'react';
import { proFetch } from '../../utils/proApi';
import Spotlight from './Spotlight';
import { lignesPourRendu, briefPourRendu, photoAvant, empreinteRendu, renduPerime } from '../../utils/rendus';

// Toutes les photos de présentation du projet, en une fois, une fois le style
// arrêté : pour chaque pièce qui a une photo, un rendu « après ». Le serveur
// travaille en tâche de fond (30 à 60 s par image) ; ici on suit l'avancement
// et chaque rendu se range dans sa pièce dès qu'il tombe. Les pièces déjà
// rendues sont décochées par défaut : on ne refait pas ce qui est fait.

// `auto` : chaque pièce validée pendant la session (`autoIds`), avec photo et
// sans rendu, part au rendu dès que possible. Un rendu périmé (postes ou style
// changés) est signalé et pré-coché, pas relancé seul : c'est une image payante.
export default function PhotosPresentation({ leadRef, rooms, style, clientBrief, emoji, onResult, niveauDe, auto = false, autoIds = null }) {
  const candidates = useMemo(() => rooms
    .map(r => ({ room: r, photos: (r.photos || []).filter(p => p && p.id) }))
    .filter(x => x.photos.length && !x.room.technique), [rooms]);
  const aFaire = (room) => !room.afterPhoto || renduPerime(room, rooms, style, niveauDe);
  const [choix, setChoix] = useState(() => Object.fromEntries(candidates.map(({ room }) => [room.id, aFaire(room)])));
  const [avant, setAvant] = useState(() => Object.fromEntries(candidates.map(({ room }) => [room.id, photoAvant(room)])));
  const [tache, setTache] = useState(null);      // { id, fait, total, etape, etat }
  const [erreur, setErreur] = useState('');
  const [spot, setSpot] = useState(null);   // { photos, i } : la photo en pleine lumière
  const vus = useRef(new Set());

  // Une pièce qui gagne une photo après le montage entre dans la liste.
  useEffect(() => {
    setChoix(c => { const n = { ...c }; for (const { room } of candidates) if (n[room.id] === undefined || (aFaire(room) && !n[room.id])) n[room.id] = aFaire(room); return n; });
    setAvant(a => { const n = { ...a }; for (const { room } of candidates) if (!n[room.id]) n[room.id] = photoAvant(room); return n; });
  }, [candidates]); // eslint-disable-line react-hooks/exhaustive-deps

  const retenues = candidates.filter(({ room }) => choix[room.id]);

  const lancer = async (liste = retenues) => {
    if (!liste.length) return;
    setErreur(''); vus.current = new Set();
    try {
      const r = await proFetch(`/leads/${leadRef}/afters`, {
        method: 'POST',
        body: JSON.stringify({
          style: style || undefined,
          pieces: liste.map(({ room }) => {
            const lines = lignesPourRendu(room, rooms, niveauDe);
            return { roomId: room.id, nom: room.customLabel || room.label, beforePhotoId: avant[room.id] || photoAvant(room), lines,
              brief: briefPourRendu(room, clientBrief), empreinte: empreinteRendu(lines, style) };
          }),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Lancement impossible');
      setTache({ id: d.tache, fait: 0, total: d.total, etape: 'Préparation', etat: 'en_cours' });
    } catch (e) { setErreur(e.message); }
  };

  // Rendu automatique des pièces validées : un lot à la fois, quand rien ne tourne.
  const dejaLances = useRef(new Set());
  useEffect(() => {
    if (!auto || tache?.etat === 'en_cours') return;
    const lot = candidates.filter(({ room }) => room.valide && (!autoIds || autoIds.has(room.id)) && !room.afterPhoto && !dejaLances.current.has(`${room.id}:${empreinteRendu(lignesPourRendu(room, rooms, niveauDe), style)}`));
    if (!lot.length) return;
    lot.forEach(({ room }) => dejaLances.current.add(`${room.id}:${empreinteRendu(lignesPourRendu(room, rooms, niveauDe), style)}`));
    lancer(lot);
  }, [auto, candidates, tache, style, autoIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suivi : toutes les 3 s tant que ça tourne ; chaque rendu nouveau est posé sur sa pièce.
  useEffect(() => {
    if (!tache || tache.etat !== 'en_cours') return undefined;
    const t = setInterval(async () => {
      try {
        const r = await proFetch(`/leads/${leadRef}/afters/${tache.id}`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'Suivi impossible');
        for (const res of d.resultats || []) {
          const cle = `${res.roomId}:${res.id || res.erreur}`;
          if (vus.current.has(cle)) continue;
          vus.current.add(cle);
          if (res.id) onResult(res.roomId, { id: res.id, url: res.url, fromPhotoId: res.fromPhotoId, le: res.le, empreinte: res.empreinte, avant: res.avant || undefined });
        }
        setTache(x => ({ ...x, fait: d.fait, total: d.total, etape: d.etape, etat: d.etat, resultats: d.resultats, erreur: d.erreur }));
      } catch (e) { setErreur(e.message); setTache(x => ({ ...x, etat: 'erreur' })); }
    }, 3000);
    return () => clearInterval(t);
  }, [tache, leadRef, onResult]);

  if (!candidates.length) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
        <h3 className="text-sm font-bold text-slate-900">Photos de présentation</h3>
        <p className="text-xs text-slate-400 mt-1">Aucune pièce n'a de photo : ajoutez-en depuis l’écran Pièces pour produire des rendus « après ».</p>
      </div>
    );
  }

  const enCours = tache?.etat === 'en_cours';
  const pct = tache?.total ? Math.round((tache.fait / tache.total) * 100) : 0;
  const echecs = (tache?.resultats || []).filter(x => x.erreur);

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Photos de présentation</h3>
          <p className="text-xs text-slate-400">Un rendu « après » par pièce cochée, avec la palette choisie. Comptez 30 à 60 s par pièce. Chaque pièce validée part au rendu d'elle-même une fois le style choisi ; les rendus se rangent dans leur pièce même si vous changez d'écran.</p>
        </div>
        <button onClick={() => lancer()} disabled={enCours || !retenues.length}
                className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-bold">
          {enCours ? 'Génération…' : `✨ Générer ${retenues.length} photo${retenues.length > 1 ? 's' : ''}`}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
        {candidates.map(({ room, photos }) => {
          const nom = room.customLabel || room.label;
          const res = (tache?.resultats || []).find(x => x.roomId === room.id);
          return (
            <div key={room.id} className={`rounded-lg border p-2 ${choix[room.id] ? 'border-orange-200 bg-orange-50/40' : 'border-slate-200'}`}>
              <label className="flex items-center gap-2 min-w-0 cursor-pointer">
                <input type="checkbox" checked={!!choix[room.id]} disabled={enCours}
                       onChange={e => setChoix(c => ({ ...c, [room.id]: e.target.checked }))}
                       className="rounded border-slate-300 text-orange-600 focus:ring-orange-400" />
                <span className="text-sm leading-none">{emoji(room)}</span>
                <span className="text-xs font-semibold text-slate-800 truncate">{nom}</span>
                {room.afterPhoto && (renduPerime(room, rooms, style, niveauDe)
                  ? <span className="ml-auto text-[10px] font-bold text-amber-800 bg-amber-50 rounded-full px-1.5 py-0.5 shrink-0" title="Les postes ou le style ont changé depuis ce rendu">périmé</span>
                  : <span className="ml-auto text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-1.5 py-0.5 shrink-0">rendu ✓</span>)}
              </label>
              <div className="flex gap-1 mt-2 overflow-x-auto">
                {photos.map((p, i) => (
                  <span key={p.id} className="relative w-12 h-12 shrink-0">
                    <button type="button" onClick={() => setAvant(a => ({ ...a, [room.id]: p.id }))} disabled={enCours} title="Choisir comme photo « avant »"
                            className={`block w-12 h-12 rounded overflow-hidden border-2 ${avant[room.id] === p.id ? 'border-orange-500' : 'border-slate-200'}`}>
                      <img src={p.url} alt="" className="w-full h-full object-cover" />
                    </button>
                    <button type="button" title="Voir en grand" aria-label="Voir en grand"
                            onClick={() => setSpot({ photos: photos.map(x => ({ url: x.url, legende: nom })), i })}
                            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white/90 shadow text-[11px] leading-5 text-center text-slate-700 hover:bg-white">🔍</button>
                  </span>
                ))}
                {room.afterPhoto?.url && (
                  <button type="button" onClick={() => setSpot({ photos: [{ url: room.afterPhoto.url, legende: `${nom} — rendu « après »` }], i: 0 })}
                          className="relative w-12 h-12 shrink-0 rounded overflow-hidden border-2 border-emerald-400 ml-1" title="Rendu actuel — voir en grand">
                    <img src={room.afterPhoto.url} alt="" className="w-full h-full object-cover" />
                  </button>
                )}
              </div>
              {res?.erreur && <p className="text-[11px] text-red-600 mt-1">{res.erreur}</p>}
            </div>
          );
        })}
      </div>

      {tache && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3" aria-live="polite">
          <div className="flex items-center gap-3">
            <p className="text-sm font-medium text-slate-800 truncate flex-1">
              {tache.etat === 'termine' ? `Terminé — ${(tache.resultats || []).filter(x => x.id).length} rendu${(tache.resultats || []).filter(x => x.id).length > 1 ? 's' : ''}${echecs.length ? `, ${echecs.length} en échec` : ''}` : tache.etat === 'erreur' ? `Échec : ${tache.erreur || erreur}` : `${tache.etape}…`}
            </p>
            <span className="text-sm font-semibold tabular-nums text-slate-700">{tache.fait}/{tache.total}</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className={`h-full rounded-full transition-[width] duration-500 ease-out ${enCours ? 'barre-anim' : tache.etat === 'erreur' ? 'bg-red-400' : 'bg-emerald-500'}`} style={{ width: `${Math.max(3, pct)}%` }} />
          </div>
        </div>
      )}
      {erreur && !tache && <p className="text-xs text-red-600 mt-2">{erreur}</p>}
      {spot && <Spotlight photos={spot.photos} index={spot.i} onIndex={i => setSpot(s => ({ ...s, i }))} onClose={() => setSpot(null)} />}
    </div>
  );
}
